/**
 * IncomingPSTNCallModal
 *
 * App-wide modal that surfaces inbound PSTN calls routed to the user's
 * Twilio Voice client (e.g., the IVR's <Dial><Client>{user_id}</Client></Dial>
 * path).  Without this UI the SDK's `incoming` event was firing silently —
 * Simon's browser received the call but had no way to accept it, so Twilio
 * dropped the leg ~30s later. The user perceived this as "the phone
 * auto-answered and hung up on both ends".
 *
 * Mounted once at the App root so it shows on any page.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { useTwilioVoice } from '../context/TwilioVoiceContext';
import { Phone, PhoneOff, PhoneIncoming } from 'lucide-react';

const formatPhone = (raw) => {
  if (!raw) return 'Unknown';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

const IncomingPSTNCallModal = () => {
  const { incoming, acceptIncoming, rejectIncoming } = useTwilioVoice();
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!incoming) {
      setSeconds(0);
      return undefined;
    }
    setSeconds(0);
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    // NOTE: We deliberately DO NOT play a ringtone here. Twilio's Voice
    // SDK already plays its native incoming-call sound (configured via
    // device.audio.incoming(true) at init). Layering an HTML5 <audio>
    // ringer on top caused two simultaneous ring streams (the user-
    // reported "ringing tone AND calling tone" stacked together) and
    // appears to interact badly with the SDK's WebRTC audio context,
    // causing the bridge to fail ~10s after answer.
    return () => {
      clearInterval(t);
    };
  }, [incoming]);

  if (!incoming) return null;

  return (
    <Dialog
      open={!!incoming}
      // IMPORTANT: do NOT call rejectIncoming here. Radix fires
      // onOpenChange(false) when the controlled `open` prop transitions
      // from true → false, which happens AFTER acceptIncoming has
      // already done call.accept() and cleared `incoming`. The stale
      // closure captured by Radix's internal state machine then fires
      // call.reject() on the just-accepted call → Twilio sees "busy"
      // ~5s after the user tapped Answer (the user-reported "C accepts
      // but the call doesn't connect" bug). The explicit Decline
      // button below handles the only legitimate reject path; outside
      // clicks and Escape are already disabled via
      // onPointerDownOutside / onEscapeKeyDown.
      onOpenChange={() => { /* no-op */ }}
    >
      <DialogContent
        className="sm:max-w-sm rounded-3xl bg-[#101010] text-white border-0 [&>button.absolute]:hidden"
        data-testid="incoming-pstn-call-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Hidden a11y title — the visual heading sits below */}
        <DialogTitle className="sr-only">Incoming call</DialogTitle>
        <DialogDescription className="sr-only">
          Incoming phone call from {formatPhone(incoming.from)}
        </DialogDescription>

        <div className="flex flex-col items-center text-center pt-4 pb-2">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#586D52]/20 animate-ping" />
            <div className="relative w-20 h-20 rounded-full bg-[#586D52]/20 flex items-center justify-center mb-4">
              <PhoneIncoming className="w-9 h-9 text-[#9bd09b]" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#9bd09b] mb-1 font-semibold animate-pulse">
            ◉ Ringing — incoming call
          </p>
          <p className="text-2xl font-semibold mb-0.5" data-testid="incoming-from">
            {formatPhone(incoming.from)}
          </p>
          <p className="text-xs text-white/40">{`${String(Math.floor(seconds/60)).padStart(2,'0')}:${String(seconds%60).padStart(2,'0')}`}</p>
          <p className="text-[10px] text-white/40 mt-2 italic">
            Tap Answer to pick up, or Decline to send to voicemail.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <Button
            onClick={rejectIncoming}
            className="bg-[#3b1212] hover:bg-[#5a1717] text-white rounded-2xl h-14 flex flex-col items-center justify-center"
            data-testid="incoming-pstn-reject"
          >
            <PhoneOff className="w-6 h-6" />
            <span className="text-xs mt-1">Decline</span>
          </Button>
          <Button
            onClick={acceptIncoming}
            className="bg-[#1a4d1a] hover:bg-[#226022] text-white rounded-2xl h-14 flex flex-col items-center justify-center"
            data-testid="incoming-pstn-accept"
          >
            <Phone className="w-6 h-6" />
            <span className="text-xs mt-1">Answer</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IncomingPSTNCallModal;
